/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { useMemo } from "react";
import { modelExport, modelImport, InvalidModelDataError, InvalidModelVersionError } from "../exportImport";
import { IModelSpec } from "../IModelSpec";
import { ModelHandler } from "../model/ModelHandler";
import { API_URL_MODEL_SAVE } from "../../config";

/**
 * Hook that returns common model action handlers
 *
 * @param spec Model spec
 * @param handler Model handler
 */
export function useModelActions(spec: IModelSpec, handler: ModelHandler, getFilenameBase: () => string) {

	const doSave = useMemo(() => () => {

		const data = modelExport(spec, handler);

		return {
			url: API_URL_MODEL_SAVE,
			data: {
				filename: getFilenameBase() + ".json",
				model_data: data
			}
		};

	}, [ spec, handler, getFilenameBase ]);

	const handleOpen = useMemo(() => (files: FileList) => {

		if (files.length === 0) {
			return;
		}

		const reader = new FileReader();
		reader.readAsText(files[0], "UTF-8");

		reader.onload = (ev) => {
			try {

				const data = JSON.parse(ev.target.result.toString());

				try {

					modelImport(spec, handler, data);

					alert("Data byla načtena.");

				} catch (err) {

					if (err instanceof InvalidModelDataError) {
						console.error("Failed to load model:", err);
						alert("Soubor se nepodařilo načíst, protože má neplatný formát nebo je poškozen.");
					} else if (err instanceof InvalidModelVersionError) {
						console.error("Failed to load model:", err);
						alert(
							// tslint:disable-next-line: max-line-length
							`Soubor se nepodařilo načíst, protože obsahuje data jiného modelu nebo jiné verze. Soubor obsahuje data pro model: "${err.data.title}" verze "${err.data.version}".`
						);
					} else {
						console.error("Failed to load model:", err);
						alert("Soubor se nepodařilo načíst kvůli neznámé chybě. Více informací naleznete v konzoli.");
					}

				}

			} catch (err) {

				console.error("Failed to read file:", err);
				alert("Soubor se nepodařilo načíst, protože má neplatný formát nebo je poškozen.");

			}
		};

		reader.onerror = (ev) => {
			console.error("Failed to open file:", ev);
			alert("Soubor se nepodařilo načíst.");
		};

	}, [ spec, handler ]);

	const doReset = useMemo(() => () => {

		handler.reset();

	}, [ spec, handler ]);

	return {
		doSave,
		handleOpen,
		doReset
	};

}
